import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as agentTargetsApi from './agent-targets.api';

import type {
  AgentTargetListQuery,
  AgentTargetStatsQuery,
  CreateAgentTargetRequest,
  UpdateAgentTargetRequest,
} from '@utazzo/shared';

import { useCurrentUser } from '@/features/auth/use-auth';

const QK = {
  list: (query: AgentTargetListQuery) => ['agent-targets', 'list', query] as const,
  detail: (id: string) => ['agent-targets', 'detail', id] as const,
  stats: (query: AgentTargetStatsQuery) => ['agent-targets', 'stats', query] as const,
  agentOptions: (actorId: string) => ['users', 'options', 'agents', actorId] as const,
  currencyOptions: () => ['currencies', 'options'] as const,
};

export const useAgentTargetsList = (query: AgentTargetListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => agentTargetsApi.listAgentTargets(query),
    staleTime: 30_000,
  });

export const useCreateAgentTarget = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAgentTargetRequest) => agentTargetsApi.createAgentTarget(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agent-targets', 'list'] });
      toast.success('Agent target added');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add agent target'),
  });
};

export const useUpdateAgentTarget = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateAgentTargetRequest }) =>
      agentTargetsApi.updateAgentTarget(id, payload),
    onSuccess: (at) => {
      qc.invalidateQueries({ queryKey: ['agent-targets', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(at.id) });
      toast.success('Agent target updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update agent target'),
  });
};

export const useDeleteAgentTarget = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => agentTargetsApi.deleteAgentTarget(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agent-targets', 'list'] });
      toast.success('Agent target deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete agent target'),
  });
};

export const useBulkDeleteAgentTargets = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => agentTargetsApi.bulkDeleteAgentTargets(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-targets', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete agent targets'),
  });
};

/**
 * Pre-aggregated stats for the Lead Inbox business strip. Server-side
 * RBAC scopes the response - SALES_AGENT only gets `my`; SALES_LEAD
 * gets both; ADMIN/OPS/FINANCE/READ_ONLY get team = org-wide. See
 * ADR-0043.
 */
export const useAgentTargetStats = (query: AgentTargetStatsQuery) =>
  useQuery({
    queryKey: QK.stats(query),
    queryFn: () => agentTargetsApi.getAgentTargetStats(query),
    staleTime: 30_000,
  });

/** Hierarchy-scoped assignable agents for the signed-in user (ADR-0039). */
export const useAgentOptions = () => {
  const me = useCurrentUser();
  return useQuery({
    queryKey: QK.agentOptions(me?.id ?? 'pending'),
    queryFn: agentTargetsApi.listAgentOptions,
    enabled: Boolean(me?.id),
    // User list changes infrequently; cache for the session to avoid N+1 fetches
    // when the form is opened repeatedly. Keyed by actor id so a role switch
    // or re-login never serves another user's assignable-agent list.
    staleTime: 5 * 60_000,
  });
};

export const useCurrencyOptions = () =>
  useQuery({
    queryKey: QK.currencyOptions(),
    queryFn: agentTargetsApi.listCurrencyOptions,
    staleTime: 5 * 60_000,
  });

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
